import { useEffect, useMemo, useState } from 'react';
import { getClientApproval, respondClientApproval } from '../services/clientApprovalService';
import type { ApprovalData, ApprovalState } from '../clientApprovalPage.types';
import {
    buildChoiceTitle,
    buildNote,
    buildSegmentPayload,
    canSubmitResponse,
    getOpenSegments,
    getRejectedOrders,
} from '../clientApprovalPage.utils';

export function useClientApproval(token: string | undefined) {
    const [state, setState] = useState<ApprovalState>('loading');
    const [data, setData] = useState<ApprovalData | null>(null);
    const [responses, setResponses] = useState<Record<number, boolean>>({});
    const [note, setNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setState('not_found');
            return;
        }

        let cancelled = false;
        getClientApproval(token)
            .then((approval) => {
                if (cancelled) return;
                setData(approval);
                setState(approval.state || 'error_approve');
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Erro ao carregar aprovação:', err);
                setState('error_approve');
            });

        return () => {
            cancelled = true;
        };
    }, [token]);

    const segments = data?.segments ?? [];
    const lockedOrders = data?.lockedOrders ?? [];
    const openSegments = useMemo(() => getOpenSegments(segments, lockedOrders), [segments, lockedOrders]);
    const rejectedOrders = getRejectedOrders(openSegments, responses);
    const canSubmit = openSegments.length > 0 && canSubmitResponse(rejectedOrders.length, note, isSubmitting);
    const choiceTitle = buildChoiceTitle(openSegments.length, lockedOrders.length, segments.length);

    const setResponse = (order: number, approved: boolean) => {
        setResponses((prev) => ({ ...prev, [order]: approved }));
    };

    const submit = async () => {
        if (!token || !canSubmit) return;
        setIsSubmitting(true);

        try {
            const result = await respondClientApproval(
                token,
                buildSegmentPayload(openSegments, responses),
                buildNote(rejectedOrders.length, note)
            );

            if (result.result === 'success') {
                setState(result.outcome === 'correction' ? 'correction_sent' : 'success');
            } else {
                setState(result.result);
            }
        } catch (err) {
            console.error('Erro ao responder:', err);
            setState('error_approve');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        state,
        data,
        openSegments,
        lockedBy: data?.lockedBy ?? {},
        responses,
        setResponse,
        rejectedCount: rejectedOrders.length,
        note,
        setNote,
        canSubmit,
        choiceTitle,
        isSubmitting,
        submit,
    };
}
